import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

const TermsPage = () => {
  const lastUpdated = 'January 1, 2026';

  return (
    <>
      <Helmet>
        <title>Terms of Service - CalcBuildAI</title>
        <meta name="description" content="Terms of Service for CalcBuildAI. Read our terms and conditions for using our service." />
      </Helmet>

      <div className="min-h-screen bg-white py-16">
        <div className="max-w-3xl mx-auto px-4">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Terms of Service</h1>
          <p className="text-gray-500 mb-8">Last updated: {lastUpdated}</p>

          <div className="prose prose-gray max-w-none">
            <p className="lead">
              Welcome to CalcBuildAI. By accessing or using our service, you agree 
              to be bound by these Terms of Service ("Terms"). Please read them carefully.
            </p>

            <h2>1. Acceptance of Terms</h2>
            <p>
              By creating an account or using our service, you agree to these Terms and our 
              Privacy Policy. If you don't agree, you may not use our service.
            </p>

            <h2>2. Description of Service</h2>
            <p>
              CalcBuildAI provides AI-powered material calculation tools for 
              construction projects. Our service offers estimates for concrete, drywall, paint, 
              flooring, lumber, roofing, and other materials.
            </p>

            <h2>3. User Accounts</h2>
            <h3>3.1 Account Creation</h3>
            <p>
              To access certain features, you must create an account with a valid email address. 
              You are responsible for maintaining the security of your account credentials.
            </p>
            
            <h3>3.2 Account Requirements</h3>
            <ul>
              <li>You must be at least 13 years old (or 16 in the EEA)</li>
              <li>You must provide accurate information</li>
              <li>One person or entity per account</li>
              <li>You're responsible for all activity under your account</li>
            </ul>

            <h2>4. Subscription and Payment</h2>
            <h3>4.1 Free Tier</h3>
            <p>
              Free accounts include 10 calculations per calendar month. Unused calculations 
              do not roll over.
            </p>
            
            <h3>4.2 Pro Subscription</h3>
            <p>
              Pro subscriptions provide unlimited calculations and premium features. Subscriptions 
              are billed monthly or annually and automatically renew unless cancelled.
            </p>
            
            <h3>4.3 Payment</h3>
            <ul>
              <li>All payments are processed securely through Stripe</li>
              <li>Prices are in USD unless otherwise stated</li>
              <li>You authorize recurring charges for subscription renewals</li>
              <li>Price changes will be communicated 30 days in advance</li>
            </ul>
            
            <h3>4.4 Refunds</h3>
            <p>
              We offer a 30-day money-back guarantee for new Pro subscriptions. Refund requests 
              after 30 days are considered on a case-by-case basis. Contact support to request a refund.
            </p>
            
            <h3>4.5 Cancellation</h3>
            <p>
              You may cancel your subscription anytime through your account settings. Access 
              continues until the end of the current billing period.
            </p>

            <h2>5. Acceptable Use</h2>
            <p>You agree NOT to:</p>
            <ul>
              <li>Use the service for any illegal purpose</li>
              <li>Attempt to gain unauthorized access to our systems</li>
              <li>Interfere with or disrupt the service</li>
              <li>Use automated tools to scrape or copy content</li>
              <li>Share your account with others</li>
              <li>Reverse engineer or decompile our software</li>
              <li>Use the service to train competing AI models</li>
              <li>Submit false or misleading information</li>
            </ul>

            <h2>6. Calculation Accuracy</h2>
            <p>
              <strong>IMPORTANT:</strong> Our calculations are estimates based on standard 
              construction practices and AI analysis. They should be used as guidance only.
            </p>
            <ul>
              <li>Always verify calculations with a qualified professional</li>
              <li>Local codes and conditions may affect actual requirements</li>
              <li>Material prices are estimates and vary by location and time</li>
              <li>We are not responsible for purchasing decisions based on our estimates</li>
            </ul>

            <h2>7. Intellectual Property</h2>
            <h3>7.1 Our Content</h3>
            <p>
              The service, including its design, features, and content, is owned by us and 
              protected by copyright, trademark, and other laws. You may not copy, modify, 
              or distribute our content without permission.
            </p>
            
            <h3>7.2 Your Content</h3>
            <p>
              You retain ownership of your calculation inputs and outputs. By using the service, 
              you grant us a license to process and store your content to provide the service.
            </p>

            <h2>8. Limitation of Liability</h2>
            <p>
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, AI CONSTRUCTION CALCULATOR SHALL NOT BE 
              LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, 
              OR ANY LOSS OF PROFITS OR REVENUES, WHETHER INCURRED DIRECTLY OR INDIRECTLY.
            </p>
            <p>
              Our total liability for any claims under these Terms shall not exceed the amount 
              you paid us in the 12 months preceding the claim.
            </p>

            <h2>9. Disclaimer of Warranties</h2>
            <p>
              THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, 
              EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF 
              MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
            </p>

            <h2>10. Indemnification</h2>
            <p>
              You agree to indemnify and hold harmless CalcBuildAI and its 
              officers, directors, employees, and agents from any claims, damages, or expenses 
              arising from your use of the service or violation of these Terms.
            </p>

            <h2>11. Changes to Terms</h2>
            <p>
              We may modify these Terms at any time. Material changes will be communicated via 
              email or website notice. Continued use after changes constitutes acceptance.
            </p>

            <h2>12. Termination</h2>
            <p>
              We may suspend or terminate your account for violations of these Terms. You may 
              delete your account at any time through settings. Upon termination, your right 
              to use the service ends immediately.
            </p>

            <h2>13. Governing Law</h2>
            <p>
              These Terms are governed by the laws of the State of California, without regard 
              to conflict of law principles. Any disputes shall be resolved in the courts of 
              San Francisco County, California.
            </p>

            <h2>14. Severability</h2>
            <p>
              If any provision of these Terms is found unenforceable, the remaining provisions 
              will continue in effect.
            </p>

            <h2>15. Contact</h2>
            <p>For questions about these Terms, contact us at:</p>
            <ul>
              <li>Email: <a href="mailto:support@calcbuildai.com">support@calcbuildai.com</a></li>
              <li>Contact Form: <Link to="/contact">Contact Page</Link></li>
            </ul>
          </div>
        </div>
      </div>
    </>
  );
};

export default TermsPage;
